import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Mail } from "lucide-react";
import api from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";

export default function PayrollTaxPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  useEffect(() => { (async () => { setData((await api.get("/dashboard/payroll-tax", { params: { year } })).data); })(); }, [year]);

  const dl = async () => {
    const res = await api.get("/dashboard/payroll-tax/pdf", { params: { year }, responseType: "blob" });
    await savePdf(res.data, `payroll_tax_${year}.pdf`);
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="payroll-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Payroll Tax Report</h1>
          <p className="text-muted-foreground">Year-end summary by worker, broken down by pay rate.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())} className="rounded-xl min-h-[44px] w-28" data-testid="payroll-year" /></div>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-full font-bold gap-2 min-h-[44px]" data-testid="payroll-email-btn"><Mail className="w-4 h-4" /> Email</Button>
          <Button onClick={dl} className="rounded-full font-bold gap-2 min-h-[44px]" style={{ background: "hsl(var(--client))" }} data-testid="payroll-pdf-btn"><Download className="w-4 h-4" /> PDF</Button>
        </div>
      </header>

      <Card className="rounded-3xl border-2 overflow-hidden" data-testid="payroll-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left p-3">Worker</th>
                <th className="text-left p-3">ABN</th>
                <th className="text-right p-3">Wkday Day</th>
                <th className="text-right p-3">Eve</th>
                <th className="text-right p-3">Night</th>
                <th className="text-right p-3">Sleepover</th>
                <th className="text-right p-3" title="Active assistance during sleepover wake-ups">Wake-up</th>
                <th className="text-right p-3">Sat</th>
                <th className="text-right p-3">Sun</th>
                <th className="text-right p-3">Pub Hol</th>
                <th className="text-right p-3 font-extrabold">Total</th>
              </tr>
            </thead>
            <tbody>
              {data?.rows?.map((r) => (
                <tr key={r.worker_id} className="border-t" data-testid={`payroll-row-${r.worker_id}`}>
                  <td className="p-3 font-bold">{r.worker_name}</td>
                  <td className="p-3 font-mono text-xs">{r.abn}</td>
                  <td className="p-3 text-right font-mono">${r.weekday_day.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${r.weekday_evening.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${r.weekday_night.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${(r.sleepover || 0).toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${(r.wakeup_active || 0).toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${r.saturday.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${r.sunday.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${r.public_holiday.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono font-bold text-client">${r.total.toFixed(2)}</td>
                </tr>
              ))}
              {data && (
                <tr className="bg-client/5 border-t-2 border-client">
                  <td colSpan="10" className="p-3 text-right font-bold uppercase tracking-wider">Grand total</td>
                  <td className="p-3 text-right font-mono font-extrabold text-client">${data.grand_total.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <EmailRecipientDialog
        open={emailOpen} onOpenChange={setEmailOpen}
        title={`Email payroll tax report ${year}`}
        description="Pick recipients from your address book. The latest payroll tax PDF will be attached."
        onSend={async (recipients) => {
          const res = await api.get("/dashboard/payroll-tax/pdf", { params: { year }, responseType: "blob" });
          const result = await shareOrMailtoPdf({
            blob: res.data,
            filename: `payroll_tax_${year}.pdf`,
            recipients,
            subject: `Payroll tax report ${year}`,
            body: `Hi,\n\nPlease find the ${year} payroll tax report attached.`,
          });
          if (result === "shared") toast.success("Opened in your email app");
          else if (result === "mailto") toast.success(`PDF saved as “payroll_tax_${year}.pdf” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
        }}
      />
    </div>
  );
}
